// Sub-pages for FÊTES DE SAINT LÉONARD — each route reuses a shared shell
// Source content extracted from the long-scroll /presence page.
import { useEffect, useState } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import Ornament from "@/components/Ornament";
import api from "@/lib/api";
import ShareWidget from "@/components/ShareWidget";

const HERO = "/photos/prieure-cross.jpg";

const PARTNERS = [
  { id: "region-normandie", name: "Région Normandie", color: "var(--lapis)", url: "https://www.normandie.fr/" },
  { id: "abbayes-normandie", name: "Abbayes de Normandie", color: "var(--moss)", url: "https://www.abbayes-normandie.com/" },
  { id: "millenium-2027", name: "Millenium 2027", color: "var(--cinnabar)", url: "https://www.millenium2027.fr/" },
];

// Shared shell: compact hero with title + breadcrumb
function FetesShell({ title, kicker, children }) {
  const location = useLocation();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [location.pathname]);
  return (
    <div data-testid="fetes-subpage">
      <section className="relative" style={{ height: "44vh", minHeight: 320 }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.35))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-10">
          <Link to="/presence" className="font-caps text-[0.65rem] text-white/80 hover:text-white" style={{ letterSpacing: "0.25em" }}>
            ← FÊTES DE SAINT LÉONARD
          </Link>
          {kicker && (
            <div className="font-caps text-[0.7rem] mt-4 text-white/85" style={{ color: "var(--gold)", letterSpacing: "0.28em" }}>{kicker}</div>
          )}
          <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">{title}</h1>
        </div>
      </section>
      {children}
      <ShareWidget />
    </div>
  );
}

// =========================================================== ORIGINE
export function FetesOrigine() {
  const { locale } = useI18n();
  return (
    <FetesShell
      kicker={locale === "fr" ? "Le millénaire d'une donation" : "The millennium of a donation"}
      title={locale === "fr" ? "Origine" : "Origins"}
    >
      <section id="origine" className="py-20 sm:py-28" style={{ background: "var(--ink)", color: "white" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 text-center md:text-left">
            <div className="font-display leading-none" style={{ fontSize: "clamp(4rem, 14vw, 11rem)", color: "var(--gold)", letterSpacing: "-0.04em" }}>1087</div>
            <div className="font-display text-2xl mt-2" style={{ color: "rgba(255,255,255,0.6)" }}>— 2027 —</div>
            <div className="font-caps text-[0.7rem] mt-6" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
              {locale === "fr" ? "13 Septembre 1087" : "13 September 1087"}
            </div>
            <div className="font-display italic text-xl mt-2" style={{ color: "rgba(255,255,255,0.9)" }}>
              {locale === "fr" ? "L'acte fondateur" : "The founding act"}
            </div>
          </div>
          <div className="md:col-span-7 md:border-l md:pl-12" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              {locale === "fr" ? "Mille ans après Guillaume." : "A thousand years after William."}
            </h2>
            <p className="font-body text-lg mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>
              {locale === "fr"
                ? "Né en 1027 à Falaise et mort le 9 septembre 1087 à Rouen, Guillaume le Conquérant offrait, peu avant sa disparition, le Prieuré saint Léonard de Vains à l'Abbaye aux Hommes de Caen. Neuf siècles plus tard, le programme PRESENCE — à l'origine des FÊTES DE SAINT LÉONARD dans le cadre du MILLENIUM — rend cette mémoire sensible, vivante, partagée par l'art, la spiritualité, la transmission (patrimoines, traditions médiévales…) et la rencontre."
                : "Born in 1027 in Falaise and died on 9 September 1087 in Rouen, William the Conqueror granted, shortly before his death, the Priory of Saint Léonard of Vains to the Abbaye aux Hommes in Caen. Nine centuries later, the PRESENCE programme — at the origin of the FEASTS OF SAINT LÉONARD within MILLENIUM — makes this memory tangible, living, shared through art, spirituality, transmission (heritage, medieval traditions…) and encounter."}
            </p>
          </div>
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== PROGRAMME
export function FetesProgramme() {
  const { t, locale } = useI18n();
  const isFr = locale === "fr";
  const p = t.presence;
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/presence/programme")
      .then((r) => { if (!cancelled) setDays(r.data.by_day || []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const cats = isFr ? SCHEDULE_CAT_LABELS_FR : SCHEDULE_CAT_LABELS_EN;
  return (
    <FetesShell kicker={locale === "fr" ? "2-4 juillet 2027" : "2-4 July 2027"} title={locale === "fr" ? "Programme" : "Programme"}>
      {/* SECTION 1 — Programme général : thèmes des journées + fil rouge */}
      <section id="programme-general" className="py-20 sm:py-28">
        <div className="container-prieure">
          <div className="text-center mb-3">
            <span className="font-caps text-[0.65rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
              {isFr ? "PROGRAMME GÉNÉRAL · FIL ROUGE" : "GENERAL PROGRAMME · RED THREAD"}
            </span>
          </div>
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <p className="font-display italic text-xl" style={{ color: "var(--ink-2)" }}>{p.eventSubtitle}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {(p.days || []).map((day, i) => (
              <article key={i} data-testid={`day-${i}`} className="bg-white p-8 border" style={{ borderColor: "var(--border)" }}>
                <div className="font-caps text-[0.7rem] mb-2" style={{ color: "var(--cinnabar)", letterSpacing: "0.2em" }}>{day.date}</div>
                <h3 className="font-display text-2xl">{day.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 2 — Programme détaillé : activités heure par heure */}
      <section id="programme-detaille" className="py-20 sm:py-28"
               style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure max-w-4xl">
          <div className="text-center mb-3">
            <span className="font-caps text-[0.65rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.25em" }}>
              {isFr ? "PROGRAMME DÉTAILLÉ · AU FIL DES HEURES" : "DETAILED PROGRAMME · HOUR BY HOUR"}
            </span>
          </div>
          <p className="font-display italic text-center text-lg mb-12 max-w-2xl mx-auto" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Activités, ateliers, conférences, liturgies et concerts — sujet à ajustements."
              : "Activities, workshops, conferences, liturgies and concerts — subject to adjustments."}
          </p>
          {loading && <p className="font-body text-center" style={{ color: "var(--muted)" }}>…</p>}
          {!loading && days.length === 0 && (
            <div className="text-center font-body italic" style={{ color: "var(--muted)" }}>
              {isFr ? "Le programme détaillé sera publié prochainement." : "The detailed programme will be published soon."}
            </div>
          )}
          {!loading && days.map(({ day, entries }) => (
            <div key={day} data-testid={`schedule-public-day-${day}`} className="mb-12">
              <div className="deco-rule mb-6">
                <span className="font-caps text-[0.78rem]" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
                  {new Date(day).toLocaleDateString(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
              <div className="space-y-3">
                {entries.map((e) => {
                  const color = SCHEDULE_CAT_COLORS[e.category] || "var(--ink-2)";
                  return (
                    <article key={`${e.source || "s"}-${e.id}`} data-testid={`schedule-public-${e.id}`}
                             className="bg-white border p-5 flex flex-wrap gap-4"
                             style={{ borderColor: "var(--border)", borderLeftWidth: 4, borderLeftColor: color }}>
                      <div className="font-mono text-base shrink-0" style={{ color: "var(--cinnabar)", minWidth: 110 }}>
                        {e.start_time}{e.end_time ? `–${e.end_time}` : ""}
                      </div>
                      <div className="flex-1 min-w-[200px]">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                                style={{ borderColor: color, color, letterSpacing: "0.18em" }}>
                            {cats[e.category] || e.category}
                          </span>
                          {e.price_eur > 0 && (
                            <span className="font-caps text-[0.55rem] px-1.5 py-0.5 border"
                                  style={{ borderColor: "var(--cinnabar)", color: "var(--cinnabar)", letterSpacing: "0.15em" }}>
                              {e.price_eur.toFixed(2)} €
                            </span>
                          )}
                        </div>
                        <h3 className="font-display text-xl">{(isFr ? e.title?.fr : e.title?.en) || e.title?.fr || e.title?.en}</h3>
                        {(e.description?.fr || e.description?.en) && (
                          <p className="font-body text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                            {(isFr ? e.description?.fr : e.description?.en) || e.description?.fr || e.description?.en}
                          </p>
                        )}
                        {(e.location?.fr || e.location?.en) && (
                          <p className="font-body text-xs mt-1" style={{ color: "var(--muted)" }}>
                            📍 {(isFr ? e.location?.fr : e.location?.en) || e.location?.fr || e.location?.en}
                          </p>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== PRE-INSCRIPTION
// (FetesPlanningDetaille remains exported below for backward compat with /presence/planning-detaille — redirects to /presence/programme#programme-detaille)
const SCHEDULE_CAT_COLORS = {
  general: "var(--ink-2)", liturgie: "var(--lapis)", atelier: "var(--cinnabar)",
  conference: "var(--gold)", musique: "var(--moss)", repas: "var(--gold)",
  autre: "var(--muted)", event: "var(--lapis)",
};
const SCHEDULE_CAT_LABELS_FR = {
  general: "Général", liturgie: "Liturgie", atelier: "Atelier",
  conference: "Conférence", musique: "Musique", repas: "Repas",
  autre: "Autre", event: "Événement",
};
const SCHEDULE_CAT_LABELS_EN = {
  general: "General", liturgie: "Liturgy", atelier: "Workshop",
  conference: "Conference", musique: "Music", repas: "Meal",
  autre: "Other", event: "Event",
};

export function FetesPlanningDetaille() {
  // Backward compat: legacy /presence/planning-detaille route now redirects to /presence/programme
  return <Navigate to="/presence/programme#programme-detaille" replace />;
}

// =========================================================== INSCRIPTION (gated)
export function FetesInscription() {
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const [open, setOpen] = useState(null); // null=loading, true/false
  useEffect(() => {
    api.get("/config").then((r) => setOpen(!!r.data.booking_open_presence)).catch(() => setOpen(false));
  }, []);
  return (
    <FetesShell kicker={isFr ? "Inscriptions payantes" : "Paid registration"} title={isFr ? "Inscriptions" : "Registration"}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure max-w-3xl text-center">
          <Ornament className="mb-8" />
          {open === null && <p className="font-body" style={{ color: "var(--muted)" }}>…</p>}
          {open === false && (
            <>
              <p className="font-display italic text-xl mb-6" style={{ color: "var(--ink-2)" }}>
                {isFr
                  ? "Les inscriptions définitives ouvriront prochainement."
                  : "Definitive registrations will open soon."}
              </p>
              <p className="font-body mb-8" style={{ color: "var(--ink-2)" }}>
                {isFr
                  ? "En attendant, pré-inscrivez-vous pour être informé en priorité dès l'ouverture des inscriptions et du paiement en ligne."
                  : "In the meantime, pre-register to be the first to know when paid registrations open."}
              </p>
              <Link to="/presence/pre-inscription" className="btn btn-primary" data-testid="inscription-cta-prereg">
                {isFr ? "→ Aller à la pré-inscription" : "→ Go to pre-registration"}
              </Link>
            </>
          )}
          {open === true && (
            <>
              <p className="font-display italic text-xl mb-6" style={{ color: "var(--ink-2)" }}>
                {isFr ? "Les inscriptions définitives sont ouvertes." : "Definitive registrations are open."}
              </p>
              <Link to="/presence#inscription" className="btn btn-primary" data-testid="inscription-cta-book">
                {isFr ? "S'inscrire et payer" : "Register & pay"}
              </Link>
            </>
          )}
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== PRE-INSCRIPTION
// Single canonical pre-registration form for the Fêtes 2027 (no duplicate on
// the /presence landing — removed in Iter Y). Mirrors the layout shared by the
// user: split first/last name, country + city, age-bucketed party size,
// optional organisation, days of interest, accommodation preference, free notes.
const AGE_BUCKETS = [
  { key: "nb_under_6", fr: "Moins de 6 ans",          en: "Under 6" },
  { key: "nb_6_12",    fr: "6 – 12 ans",              en: "6 – 12" },
  { key: "nb_13_17",   fr: "13 – 17 ans",             en: "13 – 17" },
  { key: "nb_adult",   fr: "Adultes (18 – 64 ans)",   en: "Adults (18 – 64)" },
  { key: "nb_senior",  fr: "65 ans et plus",          en: "65 and over" },
];

const PREREG_COUNTRIES = [
  "France", "Italie", "Belgique", "Suisse", "Allemagne", "Royaume-Uni",
  "Espagne", "Pays-Bas", "Luxembourg", "Portugal", "Irlande", "Pologne",
  "États-Unis", "Canada", "Autriche", "Norvège", "Suède", "Danemark", "Autre",
];

const ACCOMMODATION_OPTIONS = {
  fr: [
    { v: "habitant", lbl: "Chez l'habitant" },
    { v: "hotel", lbl: "Hôtel" },
    { v: "congregation", lbl: "Congrégation" },
    { v: "gite", lbl: "Gîte" },
    { v: "tente", lbl: "Sous tente" },
    { v: "aucun", lbl: "Pas d'hébergement nécessaire" },
  ],
  en: [
    { v: "habitant", lbl: "With locals" },
    { v: "hotel", lbl: "Hotel" },
    { v: "congregation", lbl: "Congregation" },
    { v: "gite", lbl: "Gîte" },
    { v: "tente", lbl: "Tent" },
    { v: "aucun", lbl: "No accommodation needed" },
  ],
};

const PREREG_DAYS = [
  { key: "vendredi", fr: "Vendredi 2 (Libération)", en: "Friday 2 (Liberation)" },
  { key: "samedi",   fr: "Samedi 3 (Lumière)",      en: "Saturday 3 (Light)" },
  { key: "dimanche", fr: "Dimanche 4 (Unité)",      en: "Sunday 4 (Unity)" },
];

export function FetesPreInscription() {
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    country: "France", city: "", organization: "",
    nb_under_6: 0, nb_6_12: 0, nb_13_17: 0, nb_adult: 1, nb_senior: 0,
    accommodation: "", notes: "",
  });
  const [days, setDays] = useState([]);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((s) => ({
    ...s,
    [k]: e.target.type === "number" ? Math.max(0, parseInt(e.target.value, 10) || 0) : e.target.value,
  }));
  const toggleDay = (k) =>
    setDays((arr) => (arr.includes(k) ? arr.filter((x) => x !== k) : [...arr, k]));
  const total = AGE_BUCKETS.reduce((s, b) => s + (Number(form[b.key]) || 0), 0);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (total === 0) {
        throw new Error(isFr ? "Indiquez au moins une personne." : "Please indicate at least one person.");
      }
      await api.post("/presence/pre-registration", {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email, phone: form.phone,
        country: form.country, city: form.city, organization: form.organization,
        nb_under_6: form.nb_under_6, nb_6_12: form.nb_6_12,
        nb_13_17: form.nb_13_17, nb_adult: form.nb_adult, nb_senior: form.nb_senior,
        nb_people: total,
        accommodation: form.accommodation || null,
        interest_days: days,
        notes: form.notes,
      });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex?.message || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <FetesShell kicker={null} title={isFr ? "Pré-inscription sans engagement" : "Pre-registration — no commitment"}>
      <section id="pre-inscription" className="py-20 sm:py-28" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure max-w-4xl">
          <p className="font-display italic text-lg text-center mb-10" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Manifestez votre intérêt pour les Fêtes de saint Léonard. Aucun paiement requis à ce stade — nous vous tiendrons informés à l'ouverture des inscriptions définitives."
              : "Express your interest for the Feasts of Saint Léonard. No payment required at this stage — we will notify you when formal registrations open."}
          </p>

          {done ? (
            <div data-testid="prereg-done" className="bg-white p-12 border text-center" style={{ borderColor: "var(--border)" }}>
              <Ornament className="mb-6" />
              <h3 className="font-display text-3xl mb-3">{isFr ? "Merci !" : "Thank you!"}</h3>
              <p className="font-body" style={{ color: "var(--ink-2)" }}>
                {isFr
                  ? "Votre pré-inscription est enregistrée. Vous recevrez un email à l'ouverture des inscriptions définitives."
                  : "Your pre-registration is recorded. You will receive an email when formal registrations open."}
              </p>
            </div>
          ) : (
            <form data-testid="prereg-form" onSubmit={submit}
                  className="bg-white p-6 sm:p-10 border space-y-7"
                  style={{ borderColor: "var(--border)" }}>

              {/* Identity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <label className="block">
                  <span className="label-caps">{isFr ? "Prénom *" : "First name *"}</span>
                  <input data-testid="prereg-first-name" required
                         value={form.first_name} onChange={set("first_name")}
                         className="input-classic mt-1 w-full" />
                </label>
                <label className="block">
                  <span className="label-caps">{isFr ? "Nom *" : "Last name *"}</span>
                  <input data-testid="prereg-last-name" required
                         value={form.last_name} onChange={set("last_name")}
                         className="input-classic mt-1 w-full" />
                </label>
                <label className="block">
                  <span className="label-caps">{isFr ? "Adresse e-mail *" : "Email address *"}</span>
                  <input data-testid="prereg-email" required type="email"
                         value={form.email} onChange={set("email")}
                         className="input-classic mt-1 w-full" />
                </label>
                <label className="block">
                  <span className="label-caps">{isFr ? "Téléphone" : "Phone"}</span>
                  <input data-testid="prereg-phone" type="tel"
                         value={form.phone} onChange={set("phone")}
                         className="input-classic mt-1 w-full" />
                </label>
                <label className="block">
                  <span className="label-caps">{isFr ? "Pays *" : "Country *"}</span>
                  <select data-testid="prereg-country" required
                          value={form.country} onChange={set("country")}
                          className="input-classic mt-1 w-full" style={{ background: "transparent" }}>
                    {PREREG_COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="label-caps">{isFr ? "Ville *" : "City *"}</span>
                  <input data-testid="prereg-city" required
                         value={form.city} onChange={set("city")}
                         placeholder={isFr ? "ex. Avranches" : "e.g. Avranches"}
                         className="input-classic mt-1 w-full" />
                </label>
                <label className="block md:col-span-2">
                  <span className="label-caps">{isFr ? "Organisation (optionnel)" : "Organisation (optional)"}</span>
                  <input data-testid="prereg-organization"
                         value={form.organization} onChange={set("organization")}
                         placeholder={isFr ? "Association, organisme, structure…" : "Association, organisation, entity…"}
                         className="input-classic mt-1 w-full" />
                </label>
              </div>

              {/* Age-bucketed party size */}
              <div>
                <div className="font-caps text-[0.65rem] mb-3" style={{ color: "var(--ink)", letterSpacing: "0.22em" }}>
                  {isFr ? "NOMBRE DE PERSONNES PAR TRANCHE D'ÂGE *" : "NUMBER OF PEOPLE BY AGE GROUP *"}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                  {AGE_BUCKETS.map((b) => (
                    <label key={b.key} className="block">
                      <span className="font-body text-xs" style={{ color: "var(--muted)" }}>
                        {isFr ? b.fr : b.en}
                      </span>
                      <input data-testid={`prereg-${b.key}`}
                             type="number" min="0" max="50"
                             value={form[b.key]} onChange={set(b.key)}
                             className="input-classic mt-1 w-full text-center"
                             style={{ fontSize: "1.1rem" }} />
                    </label>
                  ))}
                </div>
                <div className="text-right mt-2 font-caps text-[0.65rem]"
                     style={{ color: total > 0 ? "var(--gold)" : "var(--muted)", letterSpacing: "0.18em" }}>
                  {isFr ? "TOTAL" : "TOTAL"} : {total}
                </div>
              </div>

              {/* Days of interest */}
              <div>
                <div className="font-caps text-[0.65rem] mb-3" style={{ color: "var(--ink)", letterSpacing: "0.22em" }}>
                  {isFr ? "JOURNÉES D'INTÉRÊT" : "DAYS OF INTEREST"}
                </div>
                <div className="flex flex-wrap gap-2">
                  {PREREG_DAYS.map((d) => (
                    <button key={d.key} type="button"
                            data-testid={`prereg-day-${d.key}`}
                            onClick={() => toggleDay(d.key)}
                            className="font-caps text-[0.62rem] px-3 py-2 transition"
                            style={{
                              background: days.includes(d.key) ? "var(--cinnabar)" : "transparent",
                              color: days.includes(d.key) ? "white" : "var(--ink)",
                              border: `1px solid ${days.includes(d.key) ? "var(--cinnabar)" : "var(--border)"}`,
                              letterSpacing: "0.14em",
                            }}>
                      {days.includes(d.key) ? "✓ " : ""}{isFr ? d.fr : d.en}
                    </button>
                  ))}
                </div>
              </div>

              {/* Accommodation */}
              <label className="block">
                <span className="label-caps">{isFr ? "Hébergement souhaité" : "Desired accommodation"}</span>
                <select data-testid="prereg-accommodation"
                        value={form.accommodation} onChange={set("accommodation")}
                        className="input-classic mt-1 w-full" style={{ background: "transparent" }}>
                  <option value="">—</option>
                  {(ACCOMMODATION_OPTIONS[locale] || ACCOMMODATION_OPTIONS.fr).map((o) => (
                    <option key={o.v} value={o.v}>{o.lbl}</option>
                  ))}
                </select>
              </label>

              {/* Notes */}
              <label className="block">
                <span className="label-caps">
                  {isFr ? "Précisions (régime alimentaire, accessibilité, attentes…)" : "Additional notes (diet, accessibility, expectations…)"}
                </span>
                <textarea data-testid="prereg-notes" rows={4}
                          value={form.notes} onChange={set("notes")}
                          className="input-classic mt-1 w-full" />
              </label>

              {err && <p className="font-body text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}

              <div className="flex justify-end pt-2">
                <button type="submit" data-testid="prereg-submit"
                        disabled={busy} className="btn btn-primary">
                  {busy ? "…" : (isFr ? "Envoyer ma pré-inscription" : "Send my pre-registration")}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== PARTENAIRES — page retirée (2026-02-17)
// La page publique /presence/partenaires a été retirée. Les partenaires restent visibles via le marquee global (PartnersMarquee).

// =========================================================== RENCONTRES EUROPEENNES DES JEUNES
export function FetesRencontresJeunes() {
  const { locale } = useI18n();
  const isFr = locale === "fr";
  return (
    <FetesShell kicker={isFr ? "Jeunesse européenne" : "European youth"}
                title={isFr ? "Rencontres européennes des jeunes" : "European Youth Encounters"}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure max-w-3xl text-center">
          <Ornament className="mb-10" />
          <p className="font-display italic text-2xl leading-snug" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Page en cours d'écriture. Le contenu des Rencontres européennes des jeunes sera publié prochainement."
              : "This page is being written. The content for the European Youth Encounters will be published shortly."}
          </p>
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== LIENS UTILES
// Modern line-art SVG icons (non-religious, secular)
const LiensIcon = {
  bed: (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-3a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v3"/><path d="M3 21v-3M21 21v-3M3 12V6M7 12V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v3"/></svg>),
  fork: (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3v8a3 3 0 0 0 3 3M11 14v7M5 3v5a3 3 0 0 0 3 3M16 3c-2 0-3 1.5-3 4v4a2 2 0 0 0 2 2h1v8"/></svg>),
  map: (p) => (<svg {...p} viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z"/><path d="M9 4v14M15 6v14"/></svg>),
};

export function FetesLiensUtiles() {
  const { locale } = useI18n();
  const isFr = locale === "fr";
  const cards = [
    {
      Icon: LiensIcon.bed,
      title: isFr ? "Où dormir" : "Where to sleep",
      body: isFr
        ? "Hôtels, gîtes et chambres d'hôtes à Vains, Avranches et autour du Mont-Saint-Michel. Liste recommandée transmise après inscription."
        : "Hotels, B&Bs and guest houses in Vains, Avranches and around Mont-Saint-Michel. Recommended list sent after registration.",
    },
    {
      Icon: LiensIcon.fork,
      title: isFr ? "Où manger" : "Where to eat",
      body: isFr
        ? "Repas champêtres sur le pré du Prieuré, food trucks, et pauses gourmandes assurées par les bénévoles tout au long des trois jours."
        : "Open-air meals on the meadow, food trucks and convivial breaks throughout the three days.",
    },
    {
      Icon: LiensIcon.map,
      title: isFr ? "Y venir & navettes gratuites" : "How to get there & free shuttles",
      body: isFr
        ? "Navettes gratuites en continu pendant les trois jours depuis la gare d'Avranches, le Mont-Saint-Michel et le parking-relais de Saint-Jean-le-Thomas. Accès voiture, train, car, avion et vélo détaillés sur la page Accès."
        : "Free shuttles running continuously throughout the three days from Avranches train station, Mont-Saint-Michel and Saint-Jean-le-Thomas park-and-ride. Car, train, bus, plane and bike access detailed on the Access page.",
      cta: { to: "/acces", label: isFr ? "Voir l'accès au Prieuré" : "See access to the Priory" },
    },
  ];
  return (
    <FetesShell kicker={isFr ? "Infos pratiques" : "Practical info"} title={isFr ? "Bon à savoir" : "Good to know"}>
      <section className="py-20 sm:py-28" style={{ background: "var(--parchment)" }}>
        <div className="container-prieure">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {cards.map(({ Icon: I, title, body, cta }, i) => (
              <article key={i} className="bg-white p-8 border flex flex-col" style={{ borderColor: "var(--border)" }}>
                <div className="mb-5" style={{ color: "var(--gold)" }}><I /></div>
                <h3 className="font-display text-2xl mb-3">{title}</h3>
                <p className="font-body" style={{ color: "var(--ink-2)" }}>{body}</p>
                {cta && (
                  <Link to={cta.to} data-testid="lien-acces-prieure"
                        className="font-caps text-[0.65rem] mt-5 inline-flex items-center gap-2 transition-colors"
                        style={{ color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
                    {cta.label} →
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== CONTACTS
export function FetesContacts() {
  const { locale } = useI18n();
  return (
    <FetesShell kicker={locale === "fr" ? "Pour toute question" : "For any question"} title={locale === "fr" ? "Contacts" : "Contacts"}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure max-w-2xl text-center">
          <Ornament className="mb-8" />
          <p className="font-display italic text-lg" style={{ color: "var(--ink-2)" }}>
            {locale === "fr"
              ? "Les inscriptions définitives ouvriront prochainement. En attendant, pré-inscrivez-vous pour être informé en priorité."
              : "Definitive registration will open soon. In the meantime, pre-register to be informed first."}
          </p>
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== ARTISTES
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

const FUNCTION_LABELS_EN = {
  fresque: "Fresco",
  sculpture: "Sculpture",
  musique: "Music",
};

// SVG icons for socials — small, line-art, color-inherit
const Icon = {
  globe: (p) => (<svg {...p} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg>),
  instagram: (p) => (<svg {...p} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.8" fill="currentColor" stroke="none"/></svg>),
  youtube: (p) => (<svg {...p} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="5" width="20" height="14" rx="3"/><path d="m10 9 5 3-5 3z" fill="currentColor" stroke="none"/></svg>),
  facebook: (p) => (<svg {...p} viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M14 7h3V4h-3a4 4 0 0 0-4 4v3H7v3h3v7h3v-7h3l1-3h-4V8a1 1 0 0 1 1-1z"/></svg>),
};

const normalizeUrl = (u) => {
  if (!u) return "";
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
};

function SocialLinks({ artist }) {
  const links = [
    { key: "website", icon: Icon.globe, url: artist.website, label: "Site" },
    { key: "instagram", icon: Icon.instagram, url: artist.instagram, label: "Instagram" },
    { key: "youtube", icon: Icon.youtube, url: artist.youtube, label: "YouTube" },
    { key: "facebook", icon: Icon.facebook, url: artist.facebook, label: "Facebook" },
  ].filter((l) => l.url);
  if (links.length === 0) return null;
  return (
    <div className="flex items-center gap-3 mt-4 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
      {links.map(({ key, icon: I, url, label }) => (
        <a
          key={key}
          href={normalizeUrl(url)}
          target="_blank"
          rel="noreferrer noopener"
          title={label}
          aria-label={`${artist.name} — ${label}`}
          data-testid={`artiste-${artist.id}-${key}`}
          className="inline-flex items-center justify-center w-8 h-8 border transition-colors"
          style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "var(--cinnabar)"; e.currentTarget.style.borderColor = "var(--cinnabar)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "var(--ink-2)"; e.currentTarget.style.borderColor = "var(--border)"; }}
        >
          <I />
        </a>
      ))}
    </div>
  );
}

function GalleryGrid({ artist, locale }) {
  const isFr = locale === "fr";
  const items = Array.isArray(artist.gallery) ? artist.gallery : [];
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const open = (i) => setLightboxIdx(i);
  const close = () => setLightboxIdx(null);
  const prev = (e) => { e?.stopPropagation?.(); setLightboxIdx((i) => (i > 0 ? i - 1 : items.length - 1)); };
  const next = (e) => { e?.stopPropagation?.(); setLightboxIdx((i) => (i < items.length - 1 ? i + 1 : 0)); };
  useEffect(() => {
    if (lightboxIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i > 0 ? i - 1 : items.length - 1));
      else if (e.key === "ArrowRight") setLightboxIdx((i) => (i < items.length - 1 ? i + 1 : 0));
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, items.length]);
  if (items.length === 0) return null;
  const main = items[0];
  const thumbs = items.slice(1, 4); // up to 3 small thumbs under the main photo
  return (
    <div className="mt-5" data-testid={`artiste-gallery-${artist.id}`}>
      <div className="font-caps text-[0.6rem] mb-2"
           style={{ color: "var(--ink)", letterSpacing: "0.22em", opacity: 0.7 }}>
        {isFr ? "Au prieuré" : "At the priory"}
      </div>
      <button type="button" onClick={() => open(0)}
              data-testid={`artiste-gallery-main-${artist.id}`}
              className="block w-full overflow-hidden border"
              style={{ borderColor: "var(--border)", aspectRatio: "16 / 10", background: "var(--parchment-2)" }}>
        <img src={main.url} alt={main.caption || artist.name}
             className="w-full h-full object-cover transition-transform duration-700"
             style={{ display: "block" }}
             loading="lazy" />
      </button>
      {thumbs.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mt-2">
          {thumbs.map((g, idx) => (
            <button key={`${g.url}-${idx}`} type="button" onClick={() => open(idx + 1)}
                    data-testid={`artiste-gallery-thumb-${artist.id}-${idx}`}
                    className="overflow-hidden border"
                    style={{ borderColor: "var(--border)", aspectRatio: "1 / 1", background: "var(--parchment-2)" }}>
              <img src={g.url} alt={g.caption || `${artist.name} — ${idx + 2}`}
                   className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
      {lightboxIdx !== null && items[lightboxIdx] && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center"
             onClick={close}
             data-testid={`artiste-gallery-lightbox-${artist.id}`}
             style={{ background: "rgba(20,16,12,0.92)" }}>
          <button type="button" onClick={close} aria-label="Close"
                  className="absolute top-4 right-4 w-10 h-10 inline-flex items-center justify-center font-display text-2xl"
                  style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }}>×</button>
          {items.length > 1 && (
            <>
              <button type="button" onClick={prev} aria-label="Previous"
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center font-display text-3xl"
                      style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }}>‹</button>
              <button type="button" onClick={next} aria-label="Next"
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 inline-flex items-center justify-center font-display text-3xl"
                      style={{ color: "white", border: "1px solid rgba(255,255,255,0.3)" }}>›</button>
            </>
          )}
          <div onClick={(e) => e.stopPropagation()} className="max-w-5xl w-full px-6">
            <img src={items[lightboxIdx].url}
                 alt={items[lightboxIdx].caption || artist.name}
                 className="w-full max-h-[80vh] object-contain" />
            {items[lightboxIdx].caption && (
              <p className="font-display italic text-center mt-4" style={{ color: "rgba(255,255,255,0.85)" }}>
                {items[lightboxIdx].caption}
              </p>
            )}
            <p className="text-center mt-2 font-caps text-[0.65rem]"
               style={{ color: "rgba(255,255,255,0.55)", letterSpacing: "0.22em" }}>
              {lightboxIdx + 1} / {items.length}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function CustomLinksList({ artist, locale }) {
  const isFr = locale === "fr";
  const links = Array.isArray(artist.links) ? artist.links : [];
  if (links.length === 0) return null;
  return (
    <div className="mt-5" data-testid={`artiste-links-${artist.id}`}>
      <div className="font-caps text-[0.6rem] mb-2"
           style={{ color: "var(--ink)", letterSpacing: "0.22em", opacity: 0.7 }}>
        {isFr ? "À découvrir" : "Discover"}
      </div>
      <ul className="space-y-1.5">
        {links.map((lk, idx) => (
          <li key={`${lk.url}-${idx}`} className="font-body text-sm">
            <a href={normalizeUrl(lk.url)} target="_blank" rel="noreferrer noopener"
               data-testid={`artiste-link-${artist.id}-${idx}`}
               className="link-underline inline-flex items-center gap-2"
               style={{ color: "var(--cinnabar)" }}>
              <span aria-hidden="true" style={{ opacity: 0.6 }}>↗</span>
              {lk.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArtisteCard({ artist, locale }) {
  const photo = resolveAsset(artist.photo_url);
  const initials = (artist.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      data-testid={`artiste-card-${artist.id}`}
      className="flex flex-col bg-white border overflow-hidden"
      style={{ borderColor: "var(--border)" }}
    >
      <div
        className="relative w-full"
        style={{ aspectRatio: "4 / 5", background: "var(--parchment-2)" }}
      >
        {photo ? (
          <img src={photo} alt={artist.name} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center font-display"
            style={{ color: "var(--gold)", fontSize: "4rem" }}
          >
            {initials}
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="font-caps text-[0.6rem]" style={{ color: "var(--cinnabar)", letterSpacing: "0.2em" }}>
          {locale === "fr" ? artist.function_label : (FUNCTION_LABELS_EN[artist.function] || artist.function_label)}
        </div>
        <div className="font-display text-2xl mt-1">{artist.name}</div>
        {(artist.city || artist.country) && (
          <div className="font-body text-sm mt-1" style={{ color: "var(--muted)" }}>
            {[artist.city, artist.country].filter(Boolean).join(", ")}
          </div>
        )}
        {artist.bio && (
          <p className="font-body text-sm mt-4 leading-relaxed" style={{ color: "var(--ink-2)" }}>
            {artist.bio}
          </p>
        )}
        {artist.slug && (
          <div className="mt-4">
            <Link to={`/passeurs/${artist.slug}`}
                  data-testid={`artiste-detail-link-${artist.id}`}
                  className="font-caps text-[0.62rem] link-underline"
                  style={{ color: "var(--cinnabar)", letterSpacing: "0.22em" }}>
              {locale === "fr" ? "DÉCOUVRIR LA FICHE →" : "VIEW FULL PROFILE →"}
            </Link>
          </div>
        )}
        <GalleryGrid artist={artist} locale={locale} />
        <CustomLinksList artist={artist} locale={locale} />
        <div className="flex-1" />
        <SocialLinks artist={artist} />
      </div>
    </div>
  );
}

export function FetesArtistes() {
  const { locale } = useI18n();
  const [data, setData] = useState({ items: [], grouped: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/presence/artistes")
      .then((r) => mounted && setData(r.data || { items: [], grouped: [] }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const isFr = locale === "fr";

  return (
    <FetesShell
      kicker={isFr ? "Les voix et les mains des Fêtes" : "Voices & hands of the festivities"}
      title={isFr ? "Passeurs de Lumière" : "Bearers of Light"}
    >
      <section className="py-20 sm:py-28">
        <div className="container-prieure">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Ornament className="mb-6" />
            <p className="font-display italic text-xl" style={{ color: "var(--ink-2)" }}>
              {isFr
                ? "Musiciens, iconographes, sculpteurs, chœurs, solistes, peintres, écrivains, créateurs de parfums, acteurs… révélateurs du monde et de notre rapport sensoriel, intellectuel et spirituel à celui-ci. Révélateurs et passeurs de Lumière !"
                : "Musicians, iconographers, sculptors, choirs, soloists, painters, writers, perfume creators, actors… revealers of the world and of our sensory, intellectual and spiritual bond to it. Revealers and bearers of Light!"}
            </p>
          </div>

          {loading && (
            <div className="text-center font-display italic" style={{ color: "var(--muted)" }}>…</div>
          )}

          {!loading && data.items.length === 0 && (
            <div className="text-center py-10 font-display italic text-xl" style={{ color: "var(--muted)" }} data-testid="artistes-empty">
              {isFr
                ? "Les artistes seront annoncés très prochainement."
                : "Artists will be announced very soon."}
            </div>
          )}

          {!loading && data.grouped.map((group) => (
            <div key={group.function} className="mb-20" data-testid={`artistes-group-${group.function}`}>
              <div className="deco-rule mb-8">
                <span className="font-caps text-[0.78rem]" style={{ color: "var(--ink)" }}>
                  {isFr ? group.label : (FUNCTION_LABELS_EN[group.function] || group.label)}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {group.items.map((a) => <ArtisteCard key={a.id} artist={a} locale={locale} />)}
              </div>
            </div>
          ))}

          <div className="max-w-2xl mx-auto text-center mt-10">
            <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
              {isFr
                ? "Vous êtes artiste et souhaitez nous rejoindre ?"
                : "Are you an artist and would like to join us?"}{" "}
              <Link to="/contact" className="link-underline" style={{ color: "var(--cinnabar)" }}>
                {isFr ? "Contactez-nous" : "Get in touch"}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </FetesShell>
  );
}

// =========================================================== PASSEURS
// « Passeurs de la Baie »
// — Conférenciers, chercheurs, témoins et autres voix invitées.
// Same model as FetesArtistes; reuses ArtisteCard.
const PASSEURS_TITLE = {
  fr: "Passeurs de la Baie",
  en: "Bearers of the Bay",
  it: "Passeurs della Baia",
  es: "Mensajeros de la Bahía",
  de: "Vermittler der Bucht",
};

const PASSEURS_KICKER = {
  fr: "Conférenciers, chercheurs, témoins",
  en: "Speakers, scholars, witnesses",
  it: "Conferenzieri, ricercatori, testimoni",
  es: "Conferenciantes, investigadores, testigos",
  de: "Redner, Forscher, Zeugen",
};

const PASSEURS_INTRO = {
  fr: "Celles et ceux qui transmettent — histoire, sciences, patrimoine, guides, savoirs, mémoires vivantes, coutumes…",
  en: "Those who transmit — history, sciences, heritage, guides, knowledge, living memories, customs…",
  it: "Coloro che trasmettono — storia, scienze, patrimonio, guide, saperi, memorie vive, costumi…",
  es: "Quienes transmiten — historia, ciencias, patrimonio, guías, saberes, memorias vivas, costumbres…",
  de: "Diejenigen, die weitergeben — Geschichte, Wissenschaft, Erbe, Führer, Wissen, lebendige Erinnerungen, Bräuche…",
};

const PASSEURS_EMPTY = {
  fr: "Les intervenants seront annoncés très prochainement.",
  en: "Contributors will be announced very soon.",
  it: "I relatori saranno annunciati molto presto.",
  es: "Los intervinientes se anunciarán muy pronto.",
  de: "Die Mitwirkenden werden in Kürze bekannt gegeben.",
};

export function FetesPasseurs() {
  const { locale } = useI18n();
  const [data, setData] = useState({ items: [], grouped: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/presence/passeurs")
      .then((r) => mounted && setData(r.data || { items: [], grouped: [] }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const t = (dict) => dict[locale] || dict.en || dict.fr;

  return (
    <FetesShell kicker={t(PASSEURS_KICKER)} title={t(PASSEURS_TITLE)}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Ornament className="mb-6" />
            <p className="font-display italic text-xl" style={{ color: "var(--ink-2)" }}>
              {t(PASSEURS_INTRO)}
            </p>
          </div>

          {loading && (
            <div className="text-center font-display italic" style={{ color: "var(--muted)" }}>…</div>
          )}

          {!loading && data.items.length === 0 && (
            <div className="text-center py-10 font-display italic text-xl" style={{ color: "var(--muted)" }} data-testid="passeurs-empty">
              {t(PASSEURS_EMPTY)}
            </div>
          )}

          {!loading && data.grouped.map((group) => (
            <div key={group.function} className="mb-20" data-testid={`passeurs-group-${group.function}`}>
              <div className="deco-rule mb-8">
                <span className="font-caps text-[0.78rem]" style={{ color: "var(--ink)" }}>
                  {group.label}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {group.items.map((a) => <ArtisteCard key={a.id} artist={a} locale={locale} />)}
              </div>
            </div>
          ))}

          <div className="max-w-2xl mx-auto text-center mt-10">
            <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
              {locale === "fr"
                ? "Vous souhaitez témoigner, intervenir, transmettre ?"
                : "Would you like to speak, share, transmit?"}{" "}
              <Link to="/contact" className="link-underline" style={{ color: "var(--cinnabar)" }}>
                {locale === "fr" ? "Contactez-nous" : "Get in touch"}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </FetesShell>
  );
}

// =============================================================================
// FetesPasseursFoi — spiritual witnesses, theologians, religious figures
// Mirrors FetesPasseurs but pulls from /api/presence/passeurs-foi
// (category=religieux only).
// =============================================================================
const PASSEURS_FOI_TITLE = {
  fr: "Passeurs de l'Indicible",
  en: "Bearers of the Ineffable",
  it: "Passeurs dell'Indicibile",
  es: "Mensajeros de lo Indecible",
  de: "Vermittler des Unaussprechlichen",
  pt: "Passeurs do Indizível",
};

const PASSEURS_FOI_KICKER = {
  fr: "Témoins du mystère, théologiens, voix de l'invisible",
  en: "Witnesses of the mystery, theologians, voices of the invisible",
  it: "Testimoni del mistero, teologi, voci dell'invisibile",
  es: "Testigos del misterio, teólogos, voces de lo invisible",
  de: "Zeugen des Geheimnisses, Theologen, Stimmen des Unsichtbaren",
  pt: "Testemunhas do mistério, teólogos, vozes do invisível",
};

const PASSEURS_FOI_INTRO = {
  fr: "Celles et ceux qui rendent sensible ce qui ne se dit pas — moines, théologiens, mystiques, poètes du sacré, témoins du mystère.",
  en: "Those who make tangible what cannot be uttered — monks, theologians, mystics, poets of the sacred, witnesses of the mystery.",
  it: "Coloro che rendono sensibile ciò che non si può dire — monaci, teologi, mistici, poeti del sacro, testimoni del mistero.",
  es: "Quienes hacen sensible lo que no se dice — monjes, teólogos, místicos, poetas de lo sagrado, testigos del misterio.",
  de: "Diejenigen, die spürbar machen, was nicht gesagt werden kann — Mönche, Theologen, Mystiker, Dichter des Heiligen, Zeugen des Geheimnisses.",
  pt: "Aqueles que tornam sensível o que não se diz — monges, teólogos, místicos, poetas do sagrado, testemunhas do mistério.",
};

const PASSEURS_FOI_EMPTY = {
  fr: "Les invités seront annoncés très prochainement.",
  en: "The guests will be announced very soon.",
  it: "Gli ospiti saranno annunciati molto presto.",
  es: "Los invitados se anunciarán muy pronto.",
  de: "Die Gäste werden in Kürze bekannt gegeben.",
  pt: "Os convidados serão anunciados em breve.",
};

export function FetesPasseursFoi() {
  const { locale } = useI18n();
  const [data, setData] = useState({ items: [], grouped: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/presence/passeurs-foi")
      .then((r) => mounted && setData(r.data || { items: [], grouped: [] }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const t = (dict) => dict[locale] || dict.en || dict.fr;

  return (
    <FetesShell kicker={t(PASSEURS_FOI_KICKER)} title={t(PASSEURS_FOI_TITLE)}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Ornament className="mb-6" />
            <p className="font-display italic text-xl" style={{ color: "var(--ink-2)" }}>
              {t(PASSEURS_FOI_INTRO)}
            </p>
          </div>

          {loading && (
            <div className="text-center font-display italic" style={{ color: "var(--muted)" }}>…</div>
          )}

          {!loading && data.items.length === 0 && (
            <div className="text-center py-10 font-display italic text-xl"
                 style={{ color: "var(--muted)" }} data-testid="passeurs-foi-empty">
              {t(PASSEURS_FOI_EMPTY)}
            </div>
          )}

          {!loading && data.grouped.map((group) => (
            <div key={group.function} className="mb-20"
                 data-testid={`passeurs-foi-group-${group.function}`}>
              <div className="deco-rule mb-8">
                <span className="font-caps text-[0.78rem]" style={{ color: "var(--ink)" }}>
                  {group.label}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {group.items.map((a) => <ArtisteCard key={a.id} artist={a} locale={locale} />)}
              </div>
            </div>
          ))}

          <div className="max-w-2xl mx-auto text-center mt-10">
            <p className="font-body text-sm" style={{ color: "var(--muted)" }}>
              {locale === "fr"
                ? "Vous êtes religieux et souhaitez participer aux Fêtes ?"
                : "Are you a religious figure who wishes to take part?"}{" "}
              <Link to="/contact" className="link-underline" style={{ color: "var(--cinnabar)" }}>
                {locale === "fr" ? "Contactez-nous" : "Get in touch"}
              </Link>
            </p>
          </div>
        </div>
      </section>
    </FetesShell>
  );
}


// =============================================================================
// FetesPasseursHistoireSciences — historians, scientists, researchers
// Pulls from /api/presence/passeurs-histoire-sciences (category=histoire_sciences)
// =============================================================================
const PASSEURS_HS_TITLE = {
  fr: "Passeurs de l'histoire et des sciences",
  en: "Bearers of History and the Sciences",
  it: "Passeurs di storia e scienze",
  es: "Mensajeros de la historia y las ciencias",
  de: "Vermittler der Geschichte und Wissenschaften",
};
const PASSEURS_HS_KICKER = {
  fr: "Historiens, archéologues, chercheurs, conservateurs",
  en: "Historians, archaeologists, researchers, curators",
  it: "Storici, archeologi, ricercatori, conservatori",
  es: "Historiadores, arqueólogos, investigadores, conservadores",
  de: "Historiker, Archäologen, Forscher, Konservatoren",
};
const PASSEURS_HS_INTRO = {
  fr: "Celles et ceux qui éclairent — historiens, archéologues, scientifiques, conservateurs et passeurs du patrimoine de la baie et des Normands.",
  en: "Those who illuminate — historians, archaeologists, scientists, curators and stewards of the bay's heritage and the Normans.",
  it: "Coloro che illuminano — storici, archeologi, scienziati, conservatori del patrimonio della baia e dei Normanni.",
  es: "Quienes iluminan — historiadores, arqueólogos, científicos, conservadores del patrimonio de la bahía y de los normandos.",
  de: "Diejenigen, die Licht bringen — Historiker, Archäologen, Wissenschaftler, Konservatoren des Erbes der Bucht und der Normannen.",
};
const PASSEURS_HS_EMPTY = {
  fr: "Les chercheurs et historiens invités seront annoncés très prochainement.",
  en: "The invited researchers and historians will be announced very soon.",
  it: "I ricercatori e gli storici ospiti saranno annunciati molto presto.",
  es: "Los investigadores e historiadores invitados se anunciarán muy pronto.",
  de: "Die eingeladenen Forscher und Historiker werden in Kürze bekannt gegeben.",
};

export function FetesPasseursHistoireSciences() {
  const { locale } = useI18n();
  const isFr = locale === "fr";
  return (
    <FetesShell kicker={isFr ? "Historiens, archéologues, chercheurs" : "Historians, archaeologists, researchers"}
                title={isFr ? "Passeurs de l'histoire et des sciences" : "Bearers of History and the Sciences"}>
      <section className="py-20 sm:py-28">
        <div className="container-prieure max-w-3xl text-center">
          <Ornament className="mb-10" />
          <p className="font-display italic text-2xl leading-snug" style={{ color: "var(--ink-2)" }}>
            {isFr
              ? "Page en cours d'écriture. Les chercheurs et historiens invités seront annoncés prochainement."
              : "This page is being written. The invited researchers and historians will be announced soon."}
          </p>
        </div>
      </section>
    </FetesShell>
  );
}


